import { FormEvent, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, KeyRound, ServerCog } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { signingRuntimeAssurance } from '../lib/signing'
import type { Installation, LicenseCase, ProtocolStatus } from '../types'

const environmentLabels: Record<Installation['environmentType'], string> = {
  PRODUCTION: '生产环境', TESTING: '测试环境', DISASTER_RECOVERY: '灾备环境',
  DEVELOPMENT: '开发环境', POC: 'POC 环境',
}
const installationStatusLabels: Record<Installation['status'], string> = {
  PENDING_ACTIVATION: '待首次激活', ACTIVE: '已激活', SUSPENDED: '已停用', DECOMMISSIONED: '已退役',
}
const architectureLabels: Record<Installation['deploymentArchitecture'], string> = {
  SINGLE_NODE: '单节点部署', CLUSTER: '集群部署',
}

type Check = { code: string; passed: boolean; detail: string }

export function FeishuLicenseIssuancePanel({ caseFile, onReturned }: {
  caseFile: LicenseCase
  onReturned: () => Promise<void>
}) {
  const license = caseFile.license
  const authorization = caseFile.commercialAuthorization
  const [installation, setInstallation] = useState<Installation>()
  const [protocol, setProtocol] = useState<ProtocolStatus>()
  const [confirmed, setConfirmed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [returnReason, setReturnReason] = useState('')
  const [returning, setReturning] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true); setError('')
    Promise.all([
      api.installation(license.plannedInstallationId || ''),
      api.protocolStatus(),
    ]).then(([result, status]) => {
      if (!active) return
      const matches = result.customerId === license.customerId
        && result.commercialAuthorizationId === license.commercialAuthorizationId
        && result.installationId === license.plannedInstallationId
        && !['SUSPENDED', 'DECOMMISSIONED'].includes(result.status)
      setInstallation(matches ? result : undefined); setProtocol(status)
    }).catch(value => {
      if (active) setError(value instanceof Error ? value.message : '签发证据或安装实例加载失败')
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [license.commercialAuthorizationId, license.customerId, license.plannedInstallationId])

  const checks = useMemo(() => issuanceChecks(caseFile, installation, protocol), [caseFile, installation, protocol])
  const ready = Boolean(installation && confirmed && checks.every(check => check.passed))

  async function issue(event: FormEvent) {
    event.preventDefault()
    if (!ready || !installation) return
    setSaving(true); setError(''); setSuccess('')
    try {
      await api.issueLicense(license.licenseId, installation.installationId)
      setSuccess(`License ${license.licenseId} 已在飞书办理页完成签发。`)
    } catch (value) {
      setError(value instanceof Error ? value.message : 'License 签发失败')
    } finally {
      setSaving(false)
    }
  }

  async function returnForCorrection() {
    if (!returnReason.trim()) return
    setReturning(true); setError('')
    try {
      await api.returnLicenseForCorrection(license.licenseId, returnReason.trim())
      await onReturned()
    } catch (value) {
      setError(value instanceof Error ? value.message : '退回申请失败')
    } finally {
      setReturning(false)
    }
  }

  return <article className="feishu-task-card feishu-issuance-panel">
    <div className="panel-heading"><div><h3>在飞书中完成签发</h3><p>核对已审批权益、唯一目标实例和签名服务后，由当前签发人正式签发。</p></div><KeyRound size={19} /></div>
    <div className="feishu-issuance-boundary"><ServerCog size={18} /><p>飞书承载办理交互；权威数据校验和 ES256 签名仍在 License Center 服务端完成，签名私钥不会进入飞书。</p></div>
    <dl className="feishu-task-facts">
      <Fact label="合同 / 订单" value={license.contractNo} />
      <Fact label="产品 / Edition" value={`${license.productName} / ${license.edition}`} />
      <Fact label="授权有效期" value={`${license.startDate} 至 ${license.endDate}`} />
      <Fact label="审批人" value={license.approvedBy || '未记录'} />
      <Fact label="审批意见" value={license.approvalComment || '未记录'} />
      <Fact label="计划安装实例" value={license.plannedInstallationId || '未固化'} />
      <Fact label="权益项" value={`${license.featureSnapshot.length} 项`} />
    </dl>
    <section className="feishu-issuance-snapshot">
      <h4>已审批权益快照 · {license.featureSnapshot.length} 项</h4>
      <div className="feishu-feature-grid">{license.featureSnapshot.map(feature => <div key={feature.code}><b>{feature.name}</b><code>{feature.code}</code><small>{feature.category}{feature.requiredModuleCode ? ` · 依赖 ${feature.requiredModuleCode}` : ''}</small>{feature.description && <p>{feature.description}</p>}</div>)}</div>
    </section>
    {success ? <section className="issuance-result-card"><CheckCircle2 size={24} /><div><h4>License Artifact 已签发</h4><p>平台已完成签名生成；客户设备尚需导入 License 并完成激活回执，不能把“已签发”理解为“设备已激活”。</p><div><Link className="button button--primary" to={`/license-cases/${encodeURIComponent(license.licenseId)}`}>查看交付与设备状态</Link><Link className="button button--ghost" to="/tasks">返回任务箱</Link></div></div></section> : loading ? <div className="issuance-loading"><ServerCog size={18} />正在读取安装实例和签名服务实时状态...</div> : <form className="feishu-issuance-form" onSubmit={issue}>
      <div className="inline-notice"><ServerCog size={16} /><span><b>计划安装实例：{license.plannedInstallationId || '未固化'}</b><br />该实例已在申请阶段固化并完成审批，签发阶段不能修改。</span></div>
      {!installation && <div className="inline-notice"><AlertTriangle size={16} />固化的计划安装实例不存在、已停用/退役，或不再绑定同一商业授权。当前禁止签发；若业务数据已经变化，请在本页退回申请人重新申请。</div>}
      {installation && <dl className="feishu-task-facts">
        <Fact label="实例状态" value={installation.status === 'ACTIVE' ? '已激活，可签发新修订' : '待首次激活'} />
        <Fact label="部署环境" value={environmentLabels[installation.environmentType]} />
        <Fact label="部署方式" value={architectureLabels[installation.deploymentArchitecture]} />
        <Fact label="当前 License" value={installation.currentLicenseId || '尚未绑定'} />
        <Fact label="商业授权状态" value={authorization?.status === 'ACTIVE' ? '已生效' : authorization?.status || '未读取'} />
      </dl>}
      <div className="feishu-preflight-list">{checks.map(check => <div key={check.code} className={check.passed ? 'is-passed' : 'is-blocked'}><span>{check.passed ? '通过' : '阻塞'}</span><b>{check.detail}</b></div>)}</div>
      <label className="switch-row"><input type="checkbox" checked={confirmed} disabled={!checks.every(check => check.passed)} onChange={event => setConfirmed(event.target.checked)} /><span><b>确认签发前复核</b><small>我已核对审批意见、权益快照、授权期限、目标实例和实时签名状态。</small></span></label>
      <div className="issuance-assurance"><CheckCircle2 size={16} /><span>{signingRuntimeAssurance(protocol)}</span></div>
      {error && <div className="form-error">{error}</div>}
      <button className="button button--primary button--wide" disabled={saving || !ready}>{saving ? '正在执行签名...' : '确认签发'}</button>
      <details className="issuance-return"><summary>业务信息有误，退回申请人</summary><label>退回原因<textarea value={returnReason} onChange={event => setReturnReason(event.target.value)} maxLength={500} placeholder="请说明需要修正的合同、权益、期限或计划实例信息" /></label><button type="button" className="button button--danger button--wide" disabled={returning || !returnReason.trim()} onClick={() => void returnForCorrection()}>{returning ? '正在退回...' : '退回申请人'}</button></details>
    </form>}
  </article>
}

function issuanceChecks(caseFile: LicenseCase, installation: Installation | undefined, protocol: ProtocolStatus | undefined): Check[] {
  const license = caseFile.license
  const authorization = caseFile.commercialAuthorization
  const clusterStatus = protocol?.signingRuntime?.clusterStatus?.status || ''
  const signingReady = protocol?.signingRuntime?.keyStoreStatus === 'READY'
    && !['OUT_OF_SYNC', 'INSTANCE_ID_COLLISION', 'HEARTBEAT_MISSING'].includes(clusterStatus)
  const snapshotMatches = Boolean(authorization && license.commercialAuthorizationSnapshotSha256
    && authorization.snapshotSha256 === license.commercialAuthorizationSnapshotSha256)
  const now = new Date()
  const today = new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
  const authorizationInTerm = Boolean(authorization
    && authorization.startDate <= today && authorization.endDate >= today)
  const installationAvailable = Boolean(installation
    && !['SUSPENDED', 'DECOMMISSIONED'].includes(installation.status))
  return [
    { code: 'AUTHORIZATION', passed: authorization?.status === 'ACTIVE', detail: authorization?.status === 'ACTIVE' ? '商业授权当前可用于签发' : `商业授权状态为 ${authorization?.status || '未知'}` },
    { code: 'AUTHORIZATION_TERM', passed: authorizationInTerm, detail: authorization ? `商业授权有效期：${authorization.startDate} 至 ${authorization.endDate}` : '未读取到商业授权有效期' },
    { code: 'SNAPSHOT', passed: snapshotMatches, detail: snapshotMatches ? '当前商业授权与审批快照一致' : '商业授权与审批快照不一致' },
    { code: 'INSTALLATION', passed: Boolean(installation && installation.installationId === license.plannedInstallationId), detail: installation ? `申请与审批已锁定计划实例 ${installation.installationId}` : `未读取到计划实例 ${license.plannedInstallationId || '（未固化）'}` },
    { code: 'BINDING', passed: Boolean(installation && installation.customerId === license.customerId && installation.commercialAuthorizationId === license.commercialAuthorizationId), detail: '实例必须属于同一客户和商业授权' },
    { code: 'INSTALLATION_STATUS', passed: installationAvailable, detail: installation ? `安装实例当前状态：${installationStatusLabels[installation.status]}` : '选择实例后检查当前状态' },
    { code: 'SIGNING', passed: signingReady, detail: signingReady ? '签名服务可以正常签发' : `签名服务当前不可签发${clusterStatus ? `：${clusterStatus}` : ''}` },
  ]
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>
}
